import { z } from "zod";

const accountSettingsUserWireSchema = z
  .object({
    avatar_resource_id: z.string().min(1).nullable(),
    display_name: z.string(),
    email: z.string().email().nullable(),
    email_status: z.enum(["bound", "unbound"]),
    id: z.string().min(1),
    phone_number: z.string(),
  })
  .strict();

const organizationWireSchema = z
  .object({
    can_manage_settings: z.boolean(),
    id: z.string().min(1),
    name: z.string(),
    revision: z.number().int().positive(),
  })
  .strict();

const preferencesWireSchema = z
  .object({
    currency_code: z.enum(["CNY", "USD"]),
    locale: z.literal("zh-CN"),
    revision: z.number().int().positive(),
  })
  .strict();

const securityWireSchema = z
  .object({
    has_password: z.boolean(),
    password_updated_at: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();

export const accountSettingsResponseSchema = z
  .object({
    active_organization: organizationWireSchema,
    preferences: preferencesWireSchema,
    security: securityWireSchema,
    user: accountSettingsUserWireSchema,
  })
  .strict()
  .transform((response) => ({
    activeOrganization: {
      canManageSettings: response.active_organization.can_manage_settings,
      id: response.active_organization.id,
      name: response.active_organization.name,
      revision: response.active_organization.revision,
    },
    preferences: {
      currencyCode: response.preferences.currency_code,
      locale: response.preferences.locale,
      revision: response.preferences.revision,
    },
    security: {
      hasPassword: response.security.has_password,
      passwordUpdatedAt: response.security.password_updated_at,
    },
    user: {
      avatarResourceId: response.user.avatar_resource_id,
      displayName: response.user.display_name,
      email: response.user.email,
      emailStatus: response.user.email_status,
      id: response.user.id,
      phoneNumber: response.user.phone_number,
    },
  }));

const organizationUpdateWireSchema = z
  .object({
    organization: z
      .object({
        id: z.string().min(1),
        name: z.string(),
        revision: z.number().int().positive(),
        status: z.string(),
        type: z.string(),
      })
      .strict(),
  })
  .strict();

export const organizationUpdateResponseSchema =
  organizationUpdateWireSchema.transform((response) => ({
    id: response.organization.id,
    name: response.organization.name,
    revision: response.organization.revision,
  }));

export const preferencesUpdateResponseSchema = z
  .object({ preferences: preferencesWireSchema })
  .strict()
  .transform((response) => ({
    currencyCode: response.preferences.currency_code,
    locale: response.preferences.locale,
    revision: response.preferences.revision,
  }));

export const passwordChangeResponseSchema = z
  .object({
    other_sessions_revoked: z.number().int().nonnegative(),
    status: z.literal("password_changed"),
  })
  .strict()
  .transform((response) => ({
    otherSessionsRevoked: response.other_sessions_revoked,
    status: response.status,
  }));

const uploadDescriptorWireSchema = z
  .object({
    expires_at: z.string().datetime({ offset: true }),
    method: z.string().min(1),
    signed_headers: z.record(z.string(), z.string()),
    url: z.string().url(),
  })
  .strict();

export const avatarUploadResponseSchema = z
  .object({
    descriptor: uploadDescriptorWireSchema,
    upload: z
      .object({
        expires_at: z.string().datetime({ offset: true }),
        id: z.string().min(1),
        resource_id: z.string().min(1),
        status: z.string(),
        upload_method: z.string(),
      })
      .strict(),
  })
  .strict()
  .transform((response) => ({
    descriptor: {
      expiresAt: response.descriptor.expires_at,
      method: response.descriptor.method,
      signedHeaders: response.descriptor.signed_headers,
      url: response.descriptor.url,
    },
    upload: {
      expiresAt: response.upload.expires_at,
      id: response.upload.id,
      resourceId: response.upload.resource_id,
      status: response.upload.status,
      uploadMethod: response.upload.upload_method,
    },
  }));

export const avatarCompleteResponseSchema = z
  .object({ avatar_resource_id: z.string().min(1) })
  .strict()
  .transform((response) => ({
    avatarResourceId: response.avatar_resource_id,
  }));

export const avatarAccessResponseSchema = z
  .object({
    expires_at: z.string().datetime({ offset: true }),
    resource_id: z.string().min(1),
    url: z.string().url(),
  })
  .strict()
  .transform((response) => ({
    expiresAt: response.expires_at,
    resourceId: response.resource_id,
    url: response.url,
  }));

export type AccountSettings = ReturnType<
  typeof accountSettingsResponseSchema.parse
>;
export type CurrencyCode = AccountSettings["preferences"]["currencyCode"];
export type LocaleCode = AccountSettings["preferences"]["locale"];
